// Modal Orchestrator
//
// Owns the decision of *what* page-load modal shows, and *in what order*, so
// the age gate, the email-capture modal, the welcome popup, and Klaviyo's
// onsite discount popup stop competing with each other.
//
// Tiers (highest priority wins, only one modal is ever active at a time):
//   • blocking  — the age gate. Legally required; nothing else shows until it clears.
//   • immediate — shows as soon as the blocking tier is clear (e.g. ?modal=email).
//   • passive   — promo modals that arm on a trigger (welcome popup) or are owned
//                 by a third party (Klaviyo). Only enter play once blocking +
//                 immediate are resolved.
//
// Klaviyo can't be reordered from our code (it's an app-block embed on its own
// timer), so we suppress it via a body class and "release" it when the passive
// phase begins. Selector lives in styles-modals.css.
Object.assign(window.HiSnaps, {

    modals: {
        items: {},          // id -> candidate config
        order: [],          // ids sorted by priority, desc
        active: null,       // id of the modal currently showing
        passivePhase: false,

        // Register a modal candidate.
        //   id        unique string
        //   priority  higher shows first within its phase
        //   blocking  true = age-gate-style; must clear before anything else
        //   deferred  true = passive/triggered (welcome). Armed at passive phase.
        //   eligible  () => bool — should this show on this page load?
        //   show      () => void — render + display the modal (immediate/blocking)
        //   arm       () => void — start triggers (deferred only)
        register(config) {
            this.items[config.id] = Object.assign({
                priority: 0,
                blocking: false,
                deferred: false,
                eligible: () => true,
                show: () => {},
                arm: () => {},
            }, config, { shown: false });
        },

        start() {
            // Hold Klaviyo back until we've cleared the blocking + immediate tiers.
            document.body.classList.add("suppress-klaviyo");

            this.order = Object.values(this.items)
                .sort((a, b) => b.priority - a.priority)
                .map(i => i.id);

            this.advance();
        },

        // Show the next eligible blocking/immediate candidate. When none remain,
        // hand off to the passive phase.
        advance() {
            if (this.active) return;

            for (const id of this.order) {
                const item = this.items[id];
                if (item.shown || item.deferred) continue;
                if (!item.eligible()) continue;

                this.active = id;
                item.shown = true;
                item.show();
                return;
            }

            // Nothing immediate left — but don't release while the gate is still owed.
            if (!this.hasPendingBlocking()) this.enterPassivePhase();
        },

        hasPendingBlocking() {
            return this.order.some(id => {
                const item = this.items[id];
                return item.blocking && !item.shown && item.eligible();
            });
        },

        // A deferred (passive) modal requests the floor when its trigger fires.
        // Returns true if granted — caller should then show itself.
        request(id) {
            const item = this.items[id];
            if (!item) return false;
            if (this.active) return false;
            if (this.hasPendingBlocking()) return false;
            if (!item.eligible()) return false;

            this.active = id;
            item.shown = true;
            return true;
        },

        // A modal reports that it has closed / finished.
        done(id) {
            if (this.active === id) this.active = null;
            this.advance();
        },

        // Blocking + immediate tiers are resolved: let the passive players in.
        enterPassivePhase() {
            if (this.passivePhase) return;
            this.passivePhase = true;

            // Arm any deferred candidates (welcome popup triggers, etc).
            this.order.forEach(id => {
                const item = this.items[id];
                if (item.deferred && !item.shown && item.eligible()) item.arm();
            });

            // Let Klaviyo do its thing now that the gate is behind us.
            this.releaseKlaviyo();
        },

        releaseKlaviyo() {
            document.body.classList.remove("suppress-klaviyo");
        },
    },

});
